import React, { useState } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { LoadingSpinner, Button } from '@atoms';
import { SearchInput } from '@molecules';
import { StatusTable, AdminStatusModal } from '@organisms/admin';
import type { Status } from '@organisms/admin';
import { useAuth } from '@contexts/AuthContext';
import toast from 'react-hot-toast';

export const AdminStatusesTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.isAdmin;

  // tRPC queries and mutations
  const { 
    data: statusesData, 
    isLoading, 
    error, 
    refetch 
  } = trpc.admin.getAllStatuses.useQuery();

  const deleteStatusMutation = trpc.admin.deleteStatus.useMutation({
    onSuccess: () => {
      toast.success('Status deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete status');
    },
  });

  const statuses = statusesData?.data || [];

  // Filter statuses based on search term
  const filteredStatuses = statuses.filter(status =>
    status.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    status.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (status.description && status.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sort statuses by order number
  const sortedStatuses = [...filteredStatuses].sort((a, b) => a.orderNum - b.orderNum);

  const handleCreateStatus = () => {
    setSelectedStatus(null);
    setIsEditing(false);
    setShowStatusModal(true);
  };

  const handleEditStatus = (status: Status) => {
    setSelectedStatus(status);
    setIsEditing(true);
    setShowStatusModal(true);
  };

  const handleDeleteStatus = async (status: Status) => {
    if (window.confirm(`Are you sure you want to delete the status "${status.label}"?`)) {
      await deleteStatusMutation.mutateAsync({ id: status.id });
    }
  };

  const handleModalClose = () => {
    setShowStatusModal(false);
    setSelectedStatus(null);
    setIsEditing(false);
  };

  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading statuses..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-error-500 mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Failed to load statuses
        </h3>
        <p className="text-text-secondary mb-4">
          {error.message || 'An unexpected error occurred'}
        </p>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Status Management</h2>
          <p className="text-text-secondary mt-1">
            Manage system statuses for invoices and other entities
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={handleCreateStatus}
            variant="primary"
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Status</span>
          </Button>
        )}
      </div>

      {/* Search */}
      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search statuses..."
        className="max-w-md"
      />

      {/* Statuses Table */}
      <StatusTable
        statuses={statuses}
        searchTerm={searchTerm}
        onEditStatus={isSuperAdmin ? handleEditStatus : undefined}
        onDeleteStatus={isSuperAdmin ? handleDeleteStatus : undefined}
        isDeleteDisabled={() => deleteStatusMutation.isPending}
        showActions={isSuperAdmin}
      />

      {/* Summary */}
      <div className="flex justify-between text-sm text-text-secondary">
        <span>
          Showing {sortedStatuses.length} of {statuses.length} statuses
        </span>
        <span>
          {statuses.filter(s => s.isActive).length} active, {statuses.filter(s => !s.isActive).length} inactive
        </span>
      </div>

      {/* Status Modal */}
      {showStatusModal && (
        <AdminStatusModal
          status={selectedStatus}
          isEditing={isEditing}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
